import * as Sentry from '@sentry/react';

/**
 * Utility function to capture exceptions with Sentry
 * @param error The error to capture
 * @param context Additional context information (optional)
 */
export const captureException = (error: Error, context?: Record<string, any>) => {
  if (import.meta.env.PROD) {
    Sentry.withScope((scope) => {
      if (context) {
        Object.entries(context).forEach(([key, value]) => {
          scope.setExtra(key, value);
        });
      }
      Sentry.captureException(error);
    });
  } else {
    console.error('Error captured (would be sent to Sentry in production):', error);
    if (context) {
      console.error('Additional context:', context);
    }
  }
};

/**
 * Utility function to capture user feedback
 * @param feedback User feedback data
 */
export const captureUserFeedback = (feedback: {
  name?: string;
  email?: string;
  comments: string;
}) => {
  Sentry.captureUserFeedback(feedback);
};

/**
 * Sets user information for Sentry events
 * @param userId The user's ID
 * @param email The user's email (optional)
 * @param username The user's username (optional)
 */
export const setUserContext = (userId: string, email?: string, username?: string) => {
  Sentry.setUser({
    id: userId,
    email,
    username,
  });
};

/**
 * Clears user information when a user logs out
 */
export const clearUserContext = () => {
  Sentry.setUser(null);
};

/**
 * Sets extra context data for all future errors
 * @param key The context key
 * @param value The context value
 */
export const setExtraContext = (key: string, value: any) => {
  Sentry.setExtra(key, value);
};

/**
 * Sets a tag for all future errors
 * @param key The tag key
 * @param value The tag value
 */
export const setTag = (key: string, value: string) => {
  Sentry.setTag(key, value);
};